import { ReevalSurvey } from '@chiroup/components/body/ReevalSurvey';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { SurveyQuestion } from '@chiroup/core/types/Survey.type';
import React from 'react';
import { v4 as uuid } from 'uuid';
import requestService from '../../services/request.service';

// Adapter that mounts the legacy `<ReevalSurvey>` inside the
// healthcom-schedule app. Re-evaluation surveys differ from regular
// CCS in two important ways that <ReevalSurvey> handles and
// <ChiefComplaintSurvey> does NOT:
//   1. Expands per-complaint follow-up questions from the patient's
//      existing complaints (via `createReevalQuestions`).
//   2. The "any new problems?" radio appended at the end branches into
//      a body picker on "Yes" so a new complaint can be picked up
//      mid-survey.
// Mirrors `apps/healthcom-app/src/app/components/reeval/InterReeval.tsx`
// from the legacy app — same submit shape, same auth model
// (validationCode in body, no bearer).

type Props = {
  age: number;
  ccsFollowups?: string[];
  complete: () => void;
  gender: Genders;
  requestId: string;
  patientSurveyId: string;
  kiosk?: boolean;
  language: 'en' | 'es';
  startQuestion: SurveyQuestion;
  surveyIds?: string[];
  markSurveyComplete: () => void;
  validationCode: string | number;
  prMap: { [key: string]: string };
  encounterId?: string;
  primaryColor: string;
  complaints: any[];
  customQuestions?: SurveyQuestion[];
  customCCSSurveyId?: string;
  allowAdditionalComplaints?: boolean;
};

const InterReeval: React.FC<Props> = ({
  age,
  ccsFollowups,
  complete,
  gender,
  requestId,
  patientSurveyId,
  language,
  startQuestion,
  surveyIds,
  validationCode,
  prMap,
  markSurveyComplete,
  kiosk,
  encounterId,
  primaryColor,
  complaints,
  customQuestions,
  customCCSSurveyId,
  allowAdditionalComplaints,
}: Props) => {
  // Assign stable complaintIds to any new problem-area questions the
  // patient adds via the bodypicker mid-survey. Existing complaints
  // already carry their own ids from the backend; this only fills in
  // ids for the new ones <ReevalSurvey> appends after a "Yes" to
  // the new-problems question.
  const submit = (
    questions: SurveyQuestion[],
    kioskFlag: boolean,
    spanishQuestions?: SurveyQuestion[],
    ccsFollowupSurveyIds?: string[],
    validationCodeOverride?: string | number
  ) => {
    const newComplaintIds = questions?.reduce((obj: { [key: number]: string }, q) => {
      if (!q.complaintId && q.problemArea && !obj[q.problemArea]) {
        obj[q.problemArea] = uuid();
      }

      return obj;
    }, {});

    const tagged = questions.map(q => {
      if (q.problemArea && !q.complaintId && newComplaintIds[q.problemArea]) {
        return { ...q, complaintId: newComplaintIds[q.problemArea] };
      }

      return q;
    });

    return requestService.submitSurvey(
      requestId,
      patientSurveyId,
      tagged,
      kioskFlag,
      prMap,
      spanishQuestions,
      ccsFollowupSurveyIds,
      validationCodeOverride ?? validationCode,
      encounterId
    );
  };

  return (
    <ReevalSurvey
      language={language}
      primaryColor={primaryColor}
      complete={complete}
      ccsFollowups={ccsFollowups}
      markSurveyComplete={markSurveyComplete}
      surveyIds={surveyIds}
      validationCode={validationCode}
      prMap={prMap}
      submitSurvey={submit}
      complaints={complaints || []}
      startQuestion={startQuestion}
      age={age}
      gender={gender}
      kiosk={!!kiosk}
      customQuestions={customQuestions}
      customCCSSurveyId={customCCSSurveyId}
      allowAdditionalComplaints={allowAdditionalComplaints}
    />
  );
};

export default InterReeval;
